import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, articles, siteInfo } from '@/mocks/data';

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') || 'construction';
  const activeCategory = categories.find((c) => c.id === tabParam) || categories[0];

  const categoryArticles = articles.filter((a) => a.category === activeCategory.id);

  const switchTab = useCallback(
    (tabId: string) => {
      setSearchParams({ tab: tabId });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [setSearchParams],
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [tabParam]);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '产品分类', href: '/category' },
    { label: activeCategory.name },
  ];

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${activeCategory.name} - ${siteInfo.name}`,
    description: activeCategory.description,
    url: `${siteInfo.url}/category?tab=${activeCategory.id}`,
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webpageSchema) }} />

      <Navbar />
      <ScrollToTop />

      <main className="pt-16">
        {/* Header */}
        <section className="bg-background-100 py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="mb-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
              白浆产品分类
            </h1>
            <p className="text-sm text-foreground-600 max-w-2xl">
              白浆产品覆盖建筑、造纸、食品及工业四大领域，请选择您感兴趣的分类查看详细信息与相关文章。
            </p>
          </div>
        </section>

        {/* Tab Navigation */}
        <section className="bg-background-50 border-b border-background-200/70 sticky top-16 z-30">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => switchTab(cat.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                    activeCategory.id === cat.id
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-100'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Category Detail */}
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
              {/* Left: Image + Description */}
              <div className="lg:w-2/5 flex-shrink-0">
                <div className="rounded-lg overflow-hidden mb-6">
                  <img
                    src={activeCategory.image}
                    alt={activeCategory.name}
                    className="w-full h-64 md:h-80 object-cover"
                  />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
                  {activeCategory.name}
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  {activeCategory.description}
                </p>

                {/* Features */}
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {activeCategory.features.map((feat) => (
                    <div key={feat.label} className="bg-background-100 rounded-md p-3">
                      <div className="text-xs text-foreground-500 mb-1">{feat.label}</div>
                      <div className="font-semibold text-sm text-foreground-950">{feat.value}</div>
                    </div>
                  ))}
                </div>

                {/* Applications */}
                <div>
                  <h3 className="font-semibold text-sm text-foreground-950 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
                    典型应用场景
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {activeCategory.applications.map((app) => (
                      <span key={app} className="px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium whitespace-nowrap">
                        {app}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right: Articles */}
              <div className="lg:w-3/5">
                <h3 className="font-semibold text-lg text-foreground-950 mb-5" style={{ fontFamily: "var(--font-heading)" }}>
                  相关文章（{categoryArticles.length}篇）
                </h3>

                {categoryArticles.length === 0 ? (
                  <div className="text-center py-20 text-foreground-500 text-sm">
                    暂无相关文章
                  </div>
                ) : (
                  <div className="space-y-4">
                    {categoryArticles.map((article) => (
                      <Link
                        key={article.id}
                        to={`/article/${article.slug}`}
                        className="flex flex-col sm:flex-row gap-4 bg-background-50 border border-background-200/70 rounded-lg p-4 hover:border-primary-300 transition-all cursor-pointer group"
                      >
                        <div className="sm:w-48 flex-shrink-0 h-32 rounded-md overflow-hidden">
                          <img
                            src={article.coverImage}
                            alt={article.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-foreground-950 text-sm mb-2 group-hover:text-primary-500 transition-colors line-clamp-2" style={{ fontFamily: "var(--font-heading)" }}>
                            {article.title}
                          </h4>
                          <p className="text-xs text-foreground-600 leading-relaxed line-clamp-2 mb-3">{article.excerpt}</p>
                          <div className="flex items-center gap-3 text-xs text-foreground-500">
                            <span>{article.publishedAt}</span>
                            <span>·</span>
                            <span>{article.readTime}</span>
                            <div className="flex flex-wrap gap-1.5 ml-auto">
                              {article.tags.map((tag) => (
                                <span key={tag} className="px-2 py-0.5 bg-background-100 rounded text-xs text-foreground-600">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}